/**
 * LLD Scoring utilities.
 * Pure functions for progress calculation, evaluation scoring,
 * and reference solution comparison.
 */

/**
 * Calculates progress as a whole-number percentage.
 * Returns floor(completed / total * 100).
 *
 * @param completed - Number of completed problems (0 ≤ completed ≤ total)
 * @param total - Total number of problems (must be > 0)
 * @returns Whole-number percentage in range [0, 100]
 */
export function calculateProgress(completed: number, total: number): number {
 if (total <= 0) {
  return 0;
 }
 return Math.floor((completed / total) * 100);
}

/**
 * Calculates the overall evaluation score as the arithmetic mean
 * of dimension scores, rounded to 1 decimal place.
 *
 * @param dimensionScores - Array of individual dimension scores (each 0-10)
 * @returns Arithmetic mean rounded to 1 decimal place
 */
export function calculateOverallScore(dimensionScores: number[]): number {
 if (dimensionScores.length === 0) {
  return 0;
 }
 const sum = dimensionScores.reduce((acc, score) => acc + score, 0);
 const mean = sum / dimensionScores.length;
 return Math.round(mean * 10) / 10;
}

export interface ComparisonResult {
 missing: Set<string>;
 extra: Set<string>;
}

/**
 * Compares a user's set against a reference set, returning the
 * set differences: items missing from the user's work and extra
 * items not in the reference.
 *
 * missing = referenceSet \ userSet (in reference but not in user's work)
 * extra = userSet \ referenceSet (in user's work but not in reference)
 *
 * @param userSet - The user's set of items
 * @param referenceSet - The reference/expected set of items
 * @returns Object with `missing` and `extra` sets
 */
export function compareWithReference(
 userSet: Set<string>,
 referenceSet: Set<string>
): ComparisonResult {
 const missing = new Set<string>();
 const extra = new Set<string>();

 Array.from(referenceSet).forEach((item) => {
  if (!userSet.has(item)) {
   missing.add(item);
  }
 });

 Array.from(userSet).forEach((item) => {
  if (!referenceSet.has(item)) {
   extra.add(item);
  }
 });

 return { missing, extra };
}
